
import { Link } from 'react-router-dom';
import SEOHead from '../../components/SEOHead';

const AlopeciaPage = () => {
  const symptoms = [
    '突然髪の毛が抜ける',
    '円形または楕円形の脱毛',
    '脱毛部分が複数ある',
    '眉毛やまつ毛も抜ける',
    '爪に小さなへこみがある',
    'ストレスを感じている'
  ];

  const causes = [
    {
      title: 'ストレス',
      description: '精神的・身体的ストレス',
      icon: 'ri-emotion-unhappy-line'
    },
    {
      title: '自己免疫',
      description: '免疫システムの異常',
      icon: 'ri-shield-line'
    },
    {
      title: '過労',
      description: '疲労の蓄積',
      icon: 'ri-timer-line'
    },
    {
      title: '遺伝的要因',
      description: '家族歴による影響',
      icon: 'ri-parent-line'
    }
  ];

  const treatments = [
    {
      category: '漢方薬',
      items: [
        { name: '柴胡加竜骨牡蛎湯', effect: '肝の働きを整える' },
        { name: '加味逍遙散', effect: '気血の滞りを改善' },
        { name: '当帰芍薬散', effect: '血流促進・栄養補給' }
      ]
    },
    {
      category: '生薬',
      items: [
        { name: '当帰', effect: '血液循環の改善' },
        { name: '川芎', effect: '血行促進' },
        { name: '柴胡', effect: '肝気の調整' }
      ]
    }
  ];

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "MedicalWebPage",
    "name": "円形脱毛症の漢方治療 - 一心堂薬局",
    "description": "円形脱毛症の症状、原因、漢方での治療法について詳しく解説。一心堂薬局では体質改善による根本的な治療を行います。",
    "url": `${import.meta.env.VITE_SITE_URL}/alopecia`,
    "mainEntity": {
      "@type": "MedicalCondition",
      "name": "円形脱毛症",
      "alternateName": "えんけいだつもうしょう",
      "description": "突然、円形または楕円形に髪の毛が抜ける自己免疫性の疾患",
      "possibleCause": [
        "ストレス",
        "自己免疫",
        "過労",
        "遺伝的要因"
      ],
      "signOrSymptom": [
        "突然髪の毛が抜ける",
        "円形または楕円形の脱毛",
        "脱毛部分が複数ある",
        "眉毛やまつ毛も抜ける"
      ]
    },
    "about": {
      "@type": "MedicalBusiness",
      "name": "一心堂薬局",
      "telephone": "0479-70-6065"
    },
    "specialty": "漢方薬学",
    "medicalAudience": {
      "@type": "Patient"
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-indigo-50 to-white">
      <SEOHead
        title="円形脱毛症の漢方治療 - 一心堂薬局"
        description="円形脱毛症の症状、原因、漢方での治療法について詳しく解説。一心堂薬局では体質改善による根本的な治療を行います。"
        keywords="円形脱毛症,脱毛症,漢方治療,一心堂薬局,多古町,千葉県,自己免疫,ストレス"
        canonicalUrl={`${import.meta.env.VITE_SITE_URL}/alopecia`}
        ogTitle="円形脱毛症の漢方治療 - 一心堂薬局"
        ogDescription="円形脱毛症の症状、原因、漢方での治療法について詳しく解説。体質改善による根本的な治療を行います。"
        structuredData={structuredData}
      />
      
      {/* Navigation */}
      <nav className="fixed top-0 w-full bg-white/95 backdrop-blur-sm shadow-sm z-50">
        <div className="max-w-md mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center space-x-2">
              <i className="ri-arrow-left-line text-green-800 text-xl"></i>
              <span className="text-green-800 font-medium">戻る</span>
            </Link>
            <div className="flex items-center space-x-2">
              <div className="w-8 h-8 bg-green-600 rounded-full flex items-center justify-center">
                <i className="ri-leaf-fill text-white text-sm"></i>
              </div>
              <span className="font-bold text-green-800" style={{fontFamily: 'Pacifico, serif'}}>一心堂薬局</span>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <div className="pt-20 pb-8 px-4">
        <div className="max-w-md mx-auto text-center">
          <div className="mb-6">
            <img 
              src="https://public.readdy.ai/ai/img_res/7f051712054521945d9925aa872ae4dd.jpg"
              alt="円形脱毛症のイメージ"
              className="w-full h-48 object-cover rounded-2xl shadow-lg object-top"
            />
          </div>
          <h1 className="text-2xl font-bold text-indigo-800 mb-2">
            円形脱毛症<br />
            <span className="text-lg">(えんけいだつもうしょう)</span>
          </h1>
          <p className="text-indigo-600 text-lg mb-4">
            漢方で体質改善
          </p>
          <p className="text-gray-600 text-sm leading-relaxed mb-6">
            円形脱毛症は、突然、円形または楕円形に髪の毛が抜ける自己免疫性の疾患です。ストレスや過労などが引き金となり、免疫のバランスが乱れて毛根が攻撃されることで発症します。単発型から全身型まであり、再発を繰り返すこともあります。
          </p>
        </div>
      </div>

      {/* Traditional Medicine Approach */}
      <div className="px-4 py-8 bg-white">
        <div className="max-w-md mx-auto">
          <h2 className="text-xl font-bold text-indigo-800 mb-6 text-center">
            漢方での考え方
          </h2>
          <div className="bg-indigo-50 rounded-2xl p-6">
            <p className="text-gray-700 leading-relaxed mb-4">
              漢方では「気血の滞り」や「肝の働きの乱れ」を整えることで、体質からの改善を目指します。
            </p>
            <p className="text-gray-700 leading-relaxed">
              血流を良くし、心身の緊張を和らげる処方が有効とされ、生活リズムや睡眠の見直しも重要です。
            </p>
          </div>
        </div>
      </div>

      {/* Symptoms Check */}
      <div className="px-4 py-8 bg-indigo-50">
        <div className="max-w-md mx-auto">
          <h2 className="text-xl font-bold text-indigo-800 mb-6 text-center">
            こんな症状はありませんか？
          </h2>
          <div className="space-y-3">
            {symptoms.map((symptom, index) => (
              <label key={index} className="flex items-center space-x-3 p-4 bg-white rounded-xl cursor-pointer hover:bg-indigo-50 transition-colors">
                <input 
                  type="checkbox" 
                  className="w-5 h-5 text-indigo-600 rounded opacity-0 absolute"
                />
                <div className="w-5 h-5 border-2 border-indigo-300 rounded flex items-center justify-center">
                  <i className="ri-check-line text-indigo-600 text-sm opacity-0"></i>
                </div>
                <span className="text-gray-700">{symptom}</span>
              </label>
            ))}
          </div>
          <div className="mt-6 text-center">
            <p className="text-sm text-gray-600 mb-4">
              気になる症状がある場合は、お気軽にご相談ください
            </p>
            <button className="bg-indigo-600 text-white py-3 px-8 rounded-full font-medium">
              無料相談を予約
            </button>
          </div>
        </div>
      </div>

      {/* Causes Section */}
      <div className="px-4 py-8 bg-white">
        <div className="max-w-md mx-auto">
          <h2 className="text-xl font-bold text-indigo-800 mb-6 text-center">
            主な原因
          </h2>
          <div className="grid grid-cols-2 gap-4">
            {causes.map((cause, index) => (
              <div key={index} className="bg-indigo-50 rounded-2xl p-4 text-center shadow-sm">
                <div className="w-12 h-12 bg-indigo-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <i className={`${cause.icon} text-indigo-600 text-xl`}></i>
                </div>
                <h3 className="font-semibold text-indigo-800 mb-2">{cause.title}</h3>
                <p className="text-xs text-gray-600">{cause.description}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Treatment Options */}
      <div className="px-4 py-8 bg-indigo-50">
        <div className="max-w-md mx-auto">
          <h2 className="text-xl font-bold text-indigo-800 mb-6 text-center">
            当薬局での治療法
          </h2>
          <div className="space-y-6">
            {treatments.map((treatment, index) => (
              <div key={index} className="bg-white rounded-2xl p-6">
                <h3 className="font-bold text-indigo-800 mb-4 text-center">{treatment.category}</h3>
                <div className="space-y-3">
                  {treatment.items.map((item, itemIndex) => (
                    <div key={itemIndex} className="bg-indigo-50 rounded-xl p-4">
                      <div className="flex justify-between items-start">
                        <div>
                          <h4 className="font-semibold text-gray-800">{item.name}</h4>
                          <p className="text-sm text-gray-600 mt-1">{item.effect}</p>
                        </div>
                        <i className="ri-information-line text-indigo-600 text-lg"></i>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Lifestyle Tips */}
      <div className="px-4 py-8 bg-white">
        <div className="max-w-md mx-auto">
          <h2 className="text-xl font-bold text-indigo-800 mb-6 text-center">
            日常生活でできること
          </h2>
          <div className="bg-indigo-50 rounded-2xl p-6">
            <div className="space-y-4">
              <div className="flex items-start space-x-3">
                <div className="w-8 h-8 bg-indigo-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <i className="ri-heart-line text-indigo-600"></i>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-800">ストレス管理</h4>
                  <p className="text-sm text-gray-600">リラックスできる時間を大切にしましょう</p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <div className="w-8 h-8 bg-indigo-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <i className="ri-moon-line text-indigo-600"></i>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-800">質の良い睡眠</h4>
                  <p className="text-sm text-gray-600">規則正しい生活リズムを心がけましょう</p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <div className="w-8 h-8 bg-indigo-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <i className="ri-apple-line text-indigo-600"></i>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-800">バランスの良い食事</h4>
                  <p className="text-sm text-gray-600">タンパク質やビタミンを意識した食事を</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Consultation CTA */}
      <div className="px-4 py-8 bg-indigo-50">
        <div className="max-w-md mx-auto">
          <div className="bg-gradient-to-r from-indigo-100 to-indigo-50 rounded-2xl p-6 text-center">
            <h3 className="text-xl font-bold text-indigo-800 mb-4">
              お一人で悩まずに<br />まずはご相談ください
            </h3>
            <p className="text-sm text-gray-700 mb-6">
              体質や生活習慣に合わせて、最適な治療法をご提案いたします。<br />
              初回相談は無料です。
            </p>
            <div className="space-y-3">
              <button className="w-full bg-indigo-600 text-white py-3 px-6 rounded-full font-medium">
                <i className="ri-phone-line mr-2"></i>
                電話で相談予約
              </button>
              <button className="w-full bg-white text-indigo-600 py-3 px-6 rounded-full font-medium border border-indigo-200">
                <i className="ri-chat-3-line mr-2"></i>
                LINEで相談
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Navigation */}
      <nav className="fixed bottom-0 w-full bg-white border-t border-gray-200 z-50">
        <div className="max-w-md mx-auto">
          <div className="grid grid-cols-5 py-2">
            <Link to="/" className="flex flex-col items-center justify-center py-2 text-gray-400">
              <i className="ri-home-5-line text-lg"></i>
              <span className="text-xs mt-1">ホーム</span>
            </Link>
            <button className="flex flex-col items-center justify-center py-2 text-gray-400">
              <i className="ri-medicine-bottle-line text-lg"></i>
              <span className="text-xs mt-1">業務内容</span>
            </button>
            <button className="flex flex-col items-center justify-center py-2 text-gray-400">
              <i className="ri-leaf-line text-lg"></i>
              <span className="text-xs mt-1">漢方相談</span>
            </button>
            <button className="flex flex-col items-center justify-center py-2 text-gray-400">
              <i className="ri-map-pin-line text-lg"></i>
              <span className="text-xs mt-1">アクセス</span>
            </button>
            <button className="flex flex-col items-center justify-center py-2 text-indigo-600">
              <i className="ri-scissors-cut-fill text-lg"></i>
              <span className="text-xs mt-1">脱毛相談</span>
            </button>
          </div>
        </div>
      </nav>

      <div className="h-20"></div>
    </div>
  );
};

export default AlopeciaPage;
